import { classMerge } from '@components/utilities/classMerge'

import { legacyButtonClassNames } from '@components/primitives/Button/legacyButtonClassNames'
import { reactNodeToString } from '@lib/utilities/reactNodeToString'
import type { ReactNode } from 'react'
import { IconButton } from './IconButton'

interface ButtonBaseProps {
  ariaLabel?: string
  className?: string
  disabled?: boolean
  form?: string
  forwardedRef?: React.RefObject<HTMLButtonElement>
  id?: string
  onClick?: ((event: React.MouseEvent<HTMLButtonElement>) => void) | undefined
  onMouseMove?: (event: React.MouseEvent<HTMLButtonElement>) => void
  style?: React.CSSProperties
  type?: 'button' | 'submit'
}

// `children` and `value` are mutually exclusive
export type ButtonProps =
  | (ButtonBaseProps & {
      children?: never
      value?: ReactNode
    })
  | (ButtonBaseProps & {
      children?: ReactNode
      value?: never
    })

// Define the Button function component
function ButtonComponent({
  ariaLabel,
  children,
  className,
  disabled = false,
  form,
  forwardedRef,
  id,
  onClick,
  onMouseMove,
  style,
  type = 'button',
  value,
}: ButtonProps) {
  const textValue = reactNodeToString(value)

  const buttonStyles = classMerge(
    className,
    disabled && buttonClassNames.disabled,
  )

  return (
    <button
      aria-label={ariaLabel ?? textValue}
      className={buttonStyles}
      disabled={disabled}
      form={form}
      id={id}
      onClick={onClick}
      onMouseMove={onMouseMove}
      ref={forwardedRef}
      style={style}
      tabIndex={0}
      type={type}
    >
      {value ? value : children}
    </button>
  )
}

// Trick to allow us to show Button.Icon as deprecated until it's refactored out
const Button = Object.assign(ButtonComponent, {
  /** @deprecated Please use <Button><Icon /></Button> styled with buttonClassNames.variant.color as base class */
  Icon: IconButton,
})

export default Button

/**
 * Reusable Tailwind CSS class compositions for button styling
 *
 * @property {string} disabled - Styles applied to disabled buttons
 * @property {object} primary - Primary button styles
 * @property {object} secondary - Secondary button styles
 * @property {object} outline - Outline button styles
 * @property {object} legacy - Legacy styling variants organized by appearance type
 * @property {object} legacy.accent - Softer, secondary-like appearance with color100 backgrounds
 * @property {object} legacy.fill - Primary, solid color appearance with color500 backgrounds
 * @property {object} legacy.subtle - Subtle, quiet appearance with color50 backgrounds
 * @property {object} legacy.outline - Bordered appearance with white background and colored borders
 *
 * Each variant within legacy contains color-specific classes (primary, destructive, success, etc.)
 *
 * @example
 * // Basic usage with legacy styles
 * <Button className={buttonClassNames.primary.purple}>Button text</Button>
 *
 * // Combining with additional classes
 * <Button className={classMerge(buttonClassNames.primary.purple, 'w-full')}>
 *   Success button
 * </Button>
 */

const defaultClass =
  'outline-none box-border active:bg-opacity-80 flex items-center justify-center font-bold text-base leading-none no-underline rounded-lg gap-[1.5px] px-6 py-3 h-12 focus-visible:ring-offset-2 focus-visible:ring-destructive500 focus-visible:ring-2'
// Text buttons have less height and padding
const textButtonClass = 'px-3 py-2 h-[unset]'

// Intentional formatting for readability
// prettier-ignore
export const buttonClassNames = {
  disabled:
    'disabled:bg-neutral200 disabled:active:bg-neutral200 disabled:hover:bg-neutral200 disabled:text-neutral400 disabled:border-none disabled:cursor-not-allowed',

  primary: {
    black: classMerge(defaultClass, 'bg-black text-white hover:bg-neutral800 focus-visible:ring-neutral500 active:bg-neutral800'),
    blue: classMerge(defaultClass, 'bg-blue500 text-white hover:bg-blue600 focus-visible:ring-blue500 active:bg-blue600'),
    blueFancyGradient: classMerge(defaultClass, 'relative w-full overflow-hidden rounded-full p-4 text-lg font-bold text-white',
      'bg-gradient-to-r from-[#3D218C] to-[#6366F1]',
      'before:absolute before:inset-0 before:content-[""]',
      'before:bg-[radial-gradient(circle_150px_at_var(--mouse-x,50%)_var(--mouse-y,50%),#6c6ff6_0%,transparent_100%)]',
      'before:pointer-events-none before:opacity-0 before:transition-opacity before:duration-1000',
      'hover:before:opacity-100',
    ),
    destructive: classMerge(defaultClass, 'bg-destructive600 text-white hover:bg-destructive700 focus-visible:ring-destructive500 active:bg-destructive700'),
    gradient: classMerge(defaultClass, 'bg-gradient-to-r from-blue500 to-primary600 text-white hover:bg-gradient-to-r hover:from-blue600 hover:to-primary700 focus-visible:ring-blue500 active:bg-gradient-to-r active:from-blue600 active:to-primary700'),
    purple: classMerge(defaultClass, 'bg-primary500 text-white hover:bg-primary600 focus-visible:ring-primary500 active:bg-primary600'),
    success: classMerge(defaultClass, 'bg-success600 text-white hover:bg-success700 focus-visible:ring-success600 active:bg-success700'),
  },

  secondary: {
    black: classMerge(defaultClass, 'bg-neutral200 text-black hover:bg-neutral300 focus-visible:ring-neutral500 active:bg-neutral300'),
    blue: classMerge(defaultClass, 'bg-blue50 text-blue500 hover:bg-blue100 focus-visible:ring-blue500 active:bg-blue100'),
    purple: classMerge(defaultClass, 'bg-primary100 text-primary600 hover:bg-primary200 focus-visible:ring-primary500 active:bg-primary200'),
  },

  outline: {
    black: classMerge(defaultClass, 'border-[1.5px] border-neutral500 bg-white text-neutral800 hover:bg-neutral50 focus-visible:ring-neutral500 active:bg-neutral100'),
    blue: classMerge(defaultClass, 'border-[1.5px] border-blue500 bg-white text-blue500 hover:bg-blue50 focus-visible:ring-blue500 active:bg-blue100'),
    purple: classMerge(defaultClass, 'border-[1.5px] border-primary500 bg-white text-primary600 hover:bg-primary50 focus-visible:ring-primary500 active:bg-primary100'),
    white: classMerge(defaultClass, 'border-[1.5px] border-neutral200 bg-white text-black hover:bg-neutral100 focus-visible:ring-neutral400 active:bg-neutral200'),
  },

  text: {
    black: classMerge(defaultClass, textButtonClass, 'text-neutral500 hover:bg-neutral200 hover:text-neutral900 focus-visible:bg-neutral200 focus-visible:text-neutral900 focus-visible:ring-blue400 active:bg-neutral200 active:text-neutral800'),
    blue: classMerge(defaultClass, textButtonClass, 'text-blue500 hover:bg-blue100 focus-visible:bg-blue50 focus-visible:ring-blue400 active:bg-blue100'),
    purple: classMerge(defaultClass, textButtonClass, 'text-success600 hover:bg-success100 hover:text-success800 focus-visible:bg-success50 focus-visible:ring-blue400 active:bg-success200'),
    white: classMerge(defaultClass, textButtonClass, 'text-white hover:bg-success100/20 focus-visible:bg-success50/20 focus-visible:ring-blue400 active:bg-success200/20'),
  },

  legacy: legacyButtonClassNames,
}
